import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { I18nService } from '../core/i18n.service';
import { PlayerAuthApiService } from '../core/player-auth-api.service';
import { SessionService } from '../core/session.service';

@Component({
  selector: 'app-player-login-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <section class="page">
      <h1>{{ t('player.login.title') }}</h1>

      @if (successMessage()) {
        <p class="state state--success">{{ successMessage() }}</p>
      }
      @if (errorMessage()) {
        <p class="state state--error">{{ errorMessage() }}</p>
      }

      <form (ngSubmit)="submit()" #form="ngForm" class="form">
        <label>{{ t('player.login.email') }}
          <input type="email" name="email" required [(ngModel)]="email" />
        </label>
        <label>{{ t('player.login.password') }}
          <input type="password" name="password" required [(ngModel)]="password" />
        </label>

        <button type="submit" [disabled]="loading() || !form.valid">
          {{ loading() ? t('player.login.loading') : t('player.login.submit') }}
        </button>
      </form>

      @if (canResendVerification()) {
        <button type="button" class="secondary" (click)="resendVerification()" [disabled]="resendingVerification()">
          {{ resendingVerification() ? t('player.login.resend.loading') : t('player.login.resend') }}
        </button>
      }

      <p><a routerLink="/player/register">{{ t('player.login.noAccount') }}</a></p>
    </section>
  `,
  styles: `
    .page { padding: 1rem; max-width: 520px; }
    h1 { margin: 0 0 0.75rem; font-size: 1.4rem; }
    .form { display: grid; gap: 0.75rem; margin-bottom: 0.75rem; }
    label { display: grid; gap: 0.35rem; font-size: 0.92rem; }
    input { padding: 0.55rem 0.6rem; border: 1px solid #c9d2dc; border-radius: 8px; }
    button { border: 0; border-radius: 8px; padding: 0.6rem 0.8rem; background: #1d4c9a; color: #fff; font-weight: 600; }
    button:disabled { opacity: 0.7; }
    .secondary { background: #56667a; margin-bottom: 0.75rem; }
    .state { margin: 0.4rem 0 0.75rem; }
    .state--error { color: #9d1c1c; }
    .state--success { color: #1b6a31; }
    a { color: #1d4c9a; text-decoration: none; }
  `
})
export class PlayerLoginPageComponent {
  private readonly api = inject(PlayerAuthApiService);
  private readonly i18n = inject(I18nService);
  private readonly session = inject(SessionService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  protected email = '';
  protected password = '';

  protected readonly loading = signal(false);
  protected readonly resendingVerification = signal(false);
  protected readonly canResendVerification = signal(false);
  protected readonly successMessage = signal<string | null>(null);
  protected readonly errorMessage = signal<string | null>(null);

  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }

  constructor() {
    const returnTo = this.returnToUrl();
    if (this.session.isAuthenticated()) {
      void this.router.navigateByUrl(returnTo || '/player/me');
    }
  }

  protected submit(): void {
    this.loading.set(true);
    this.successMessage.set(null);
    this.errorMessage.set(null);
    this.canResendVerification.set(false);

    this.api
      .login({
        email: this.email.trim(),
        password: this.password
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.session.setToken(response.token);
          this.session.setIdentity({
            id: response.user.id,
            pseudo: response.user.pseudo,
            email: response.user.email
          });
          this.loading.set(false);
          void this.router.navigateByUrl(this.returnToUrl() || '/player/me');
        },
        error: (error) => {
          const apiMessage = error?.error?.error as string | undefined;
          this.errorMessage.set(this.humanizeLoginError(apiMessage));
          this.canResendVerification.set(apiMessage === 'Email verification required');
          this.loading.set(false);
        }
      });
  }

  protected resendVerification(): void {
    this.resendingVerification.set(true);
    this.successMessage.set(null);
    this.errorMessage.set(null);

    this.api
      .resendEmailVerification(this.email.trim())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.successMessage.set(response.message || 'E-mail de verification renvoye.');
          this.resendingVerification.set(false);
        },
        error: () => {
          this.errorMessage.set('Impossible de renvoyer l e-mail de verification.');
          this.resendingVerification.set(false);
        }
      });
  }

  private humanizeLoginError(apiMessage?: string): string {
    if (!apiMessage) {
      return 'Connexion impossible. Reessayez.';
    }
    if (apiMessage === 'Email verification required') {
      return 'Votre e-mail doit etre verifie avant connexion.';
    }
    if (apiMessage === 'Invalid credentials') {
      return 'Identifiants invalides.';
    }
    return apiMessage;
  }

  private returnToUrl(): string | null {
    const returnTo = this.route.snapshot.queryParamMap.get('returnTo');
    if (!returnTo || !returnTo.startsWith('/')) {
      return null;
    }
    return returnTo;
  }
}
